import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLSmartLinksHomeRendererProps } from './props';
import { AccountStatsView } from '../accountStatsView/AccountStatsView';
import { SMLContainer } from '../smlContainer/SMLContainer';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { SMLPageHead } from '../../music24/components/SMLPageHead/SMLPageHead';
import { SMLCreateSmartLinkButton } from '../smlCreateSmartLinkButton/SMLCreateSmartLinkButton';
import { useFetchSmartLinkStatsSummariesQuery } from '../../../redux/api/fymAPI';
import { useSmartLinksOverviewParams } from './useSmartLinksOverviewParams';
import { SmartLinksSearchBar } from './SmartLinksSearchBar';
import { SmartLinksGridSection } from './SmartLinksGridSection';
import { SmartLinkClicks } from './SmartLinksGridCards';

const SMLSmartLinksHomeRendererComponent: React.FC<SMLSmartLinksHomeRendererProps> = ({
  account,
  onSmartLinkClick,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { tab, search, setTab, setSearch } = useSmartLinksOverviewParams();

  // One request for the whole account rather than one per card.
  const { data: summaries } = useFetchSmartLinkStatsSummariesQuery({ accountId: account.id });
  const clicksBySmartLink = useMemo(() => {
    const map: SmartLinkClicks = new Map();
    summaries?.summaries.forEach((summary) => {
      map.set(summary.smartLinkId, { clicks: summary.clicks, trend: summary.trend });
    });
    return map;
  }, [summaries]);

  const breadcrumbs = useMemo(() => {
    return [
      {
        name: intl.formatMessage({ id: 'app.sml.sidebar.section.promote', defaultMessage: 'Promote' }),
      },
      {
        name: intl.formatMessage({ id: 'app.sml.breadcrumbs.smartlinks', defaultMessage: 'SmartLinks' }),
        onClick: () => router.push(SMLRoutes.smartlinks),
      },
    ];
  }, [intl, router]);

  return (
    <SMLContainer
      header={
        <SMLPageHead
          breadcrumbs={breadcrumbs}
          title={intl.formatMessage({ id: 'app.sml.breadcrumbs.smartlinks', defaultMessage: 'SmartLinks' })}
          subtitle={intl.formatMessage({
            id: 'app.sml.smartlinks.index.sub',
            defaultMessage:
              'One page per release, built from blocks. Every tap is attributed back to the service, the market and the block that earned it.',
          })}
        >
          {/* The artist's name, avatar, address and trackers live in Settings → Artist and
              Settings → SmartLinks (design artist-settings a1, b1). */}
          <SMLCreateSmartLinkButton accountId={account.id} />
        </SMLPageHead>
      }
    >
      <AccountStatsView account={account} />
      <SmartLinksSearchBar value={search} onChange={setSearch} />
      {/* A new tab or search starts the grid from its first request again. */}
      <SmartLinksGridSection
        key={`${tab}|${search.trim()}`}
        accountId={account.id}
        mainSmartLinkId={account.mainSmartLinkId}
        tab={tab}
        search={search}
        onTabChange={setTab}
        clicksBySmartLink={clicksBySmartLink}
        onSmartLinkClick={onSmartLinkClick}
      />
    </SMLContainer>
  );
};

export const SMLSmartLinksHomeRenderer = TypedMemo(SMLSmartLinksHomeRendererComponent);
